import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    DEPTH,
    ORDER_STATUS,
    PRICE_TYPE,
    TIME_IN_FORCE,
    TRADE_TYPE,
    TRADE_TYPE_LANG_MAP,
} from 'src/common/constants';
import { contractNameFormatter, currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppSelector } from 'src/hooks';
import { FutureSymbol, TradeOrder } from 'src/types';

const FilledOrdersTable: FC = () => {
    const { t } = useTranslation();
    const [filterFilledOrders, setFilterFilledOrders] = useState([] as Array<TradeOrder>);
    const currentSymbol: FutureSymbol = useAppSelector(
        (state) => state.derivativePage.currentSymbol,
    );
    const symbolId = currentSymbol.symbolId;
    const tradeOrders: Array<TradeOrder> = useAppSelector(
        (state) => state.derivativePage.tradeOrders,
    );
    const futureSymbols = useAppSelector((state) => state.globalData.configV2.futuresSymbol);

    useEffect(() => {
        setFilterFilledOrders(
            tradeOrders
                .filter(
                    (tradeOrder) =>
                        tradeOrder.symbolId === symbolId &&
                        (tradeOrder.status === ORDER_STATUS.FILLED ||
                            tradeOrder.status === ORDER_STATUS.ORDER_FILLED),
                )
                .sort((a, b) => (a.time - b.time > 0 ? -1 : 1)),
        );
    }, [tradeOrders, symbolId]);

    const columns = [
        {
            title: (
                <FlexContainer styles={{ alignItems: 'center' }}>{t('contracts')}</FlexContainer>
            ),
            key: 'contracts',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return (
                    <div
                        className={classNames(
                            'contract',
                            {
                                borderGreen:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                borderRed:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                            {
                                greenText:
                                    row['side'] === TRADE_TYPE.BUY_OPEN ||
                                    row['side'] === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    row['side'] === TRADE_TYPE.SELL_OPEN ||
                                    row['side'] === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {contractNameFormatter(row['symbolName'])}
                    </div>
                );
            },
        },
        {
            title: t('filled_total'),
            key: 'filledTotal',
            width: '8%',
            className: 'centerText',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['executedQty']}/{row['origQty']}
                    </span>
                );
            },
        },
        {
            title: t('filled_price'),
            key: 'filledPrice',
            className: 'centerText',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['liquidationType'] === TIME_IN_FORCE.IOC ||
                        row['avgPrice'] === '0' ||
                        row['avgPrice'] === ''
                            ? row['triggerPrice']
                                ? currencyFormatter(
                                      row['triggerPrice'],
                                      DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                                  )
                                : '--'
                            : row['avgPrice']
                            ? currencyFormatter(
                                  row['avgPrice'],
                                  DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                              )
                            : '0.00'}
                    </span>
                );
            },
        },
        {
            title: t('order_price'),
            key: 'orderPrice',
            className: 'centerText',
            width: '10%',
            render: (row: { [x: string]: any }) => {
                return (
                    <span>
                        {row['price'] === '0'
                            ? t('last_traded_price')
                            : currencyFormatter(
                                  row['price'],
                                  DEPTH[futureSymbols[row.symbolId].minPricePrecision],
                              )}
                    </span>
                );
            },
        },
        {
            title: t('trade_type'),
            key: 'tradeType',
            className: 'centerText',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                const value = row['side'];
                return (
                    <span
                        className={classNames(
                            {
                                greenText:
                                    value === TRADE_TYPE.BUY_OPEN || value === TRADE_TYPE.BUY_CLOSE,
                            },
                            {
                                redText:
                                    value === TRADE_TYPE.SELL_OPEN ||
                                    value === TRADE_TYPE.SELL_CLOSE,
                            },
                        )}
                    >
                        {t(TRADE_TYPE_LANG_MAP[value])}
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            className: 'centerText',
            width: '8%',
            render: (row: { [x: string]: any }) => {
                return t(row['priceType'] === PRICE_TYPE.LIMIT ? 'limit' : 'market');
            },
        },
        {
            title: t('order_no'),
            key: 'orderNo',
            className: 'centerText',
            width: '14%',
            render: (row: { [x: string]: any }) => {
                return <span>{row['orderId']}</span>;
            },
        },
        {
            title: t('order_time'),
            key: 'orderTime',
            className: 'centerText',
            width: '11%',
            render: (row: { [x: string]: any }) => {
                return <span>{DateTimeFormatter(Number(row['time']))}</span>;
            },
        },
    ];
    return <StyledTable columns={columns} data={filterFilledOrders} rowKey="orderId" />;
};

export { FilledOrdersTable };
